import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/kit";
import Logistics from "@/pages/Logistics";
import Finance from "@/pages/Finance";
import {
  Loader2, RefreshCw, DatabaseZap, Truck, PackageCheck, Factory, ClipboardList, ClipboardCheck,
  AlertTriangle, TriangleAlert, PackageSearch, ArrowRight,
} from "lucide-react";

const TONE = {
  emerald: "bg-emerald-500", orange: "bg-orange-500", red: "bg-rose-500",
  blue: "bg-sky-500", amber: "bg-amber-500", purple: "bg-violet-500", slate: "bg-slate-500",
};

const KPI_META = {
  raw_materials_available: { l: "Raw Materials Available", t: "emerald" },
  finished_goods_available: { l: "Finished Goods Available", t: "emerald" },
  low_stock_items: { l: "Low Stock Items", t: "amber" },
  out_of_stock_items: { l: "Out of Stock Items", t: "red" },
  open_purchase_orders: { l: "Open Purchase Orders", t: "blue" },
  supplier_deliveries_expected: { l: "Deliveries Expected", t: "blue" },
  active_production_orders: { l: "Active Production Orders", t: "purple" },
  production_waiting_materials: { l: "Production Waiting Materials", t: "amber" },
  pending_material_requests: { l: "Material Requests to Issue", t: "amber" },
  open_stock_transfers: { l: "Open Stock Transfers", t: "blue" },
  open_customer_orders: { l: "Open Customer Orders", t: "blue" },
  orders_awaiting_dispatch: { l: "Orders Awaiting Dispatch", t: "orange" },
  deliveries_in_transit: { l: "Deliveries In Transit", t: "purple" },
  delayed_deliveries: { l: "Delayed Deliveries", t: "orange" },
  available_vehicles: { l: "Available Vehicles", t: "emerald" },
  vehicles_under_maintenance: { l: "Vehicles Under Maintenance", t: "red" },
};

const CONTROL_TOWER_KEYS = [
  "raw_materials_available", "finished_goods_available", "low_stock_items", "out_of_stock_items",
  "open_purchase_orders", "supplier_deliveries_expected", "active_production_orders", "production_waiting_materials",
  "pending_material_requests", "open_stock_transfers", "open_customer_orders", "orders_awaiting_dispatch",
  "deliveries_in_transit", "delayed_deliveries", "available_vehicles", "vehicles_under_maintenance",
];

function Kpi({ metaKey, value, to }) {
  const meta = KPI_META[metaKey] || { l: metaKey, t: "slate" };
  const inner = (
    <>
      <span className={`absolute top-0 left-0 h-[2px] w-full ${TONE[meta.t]}`} />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{meta.l}</div>
      <div className="font-mono text-3xl font-extrabold text-slate-100 mt-3" data-testid={`kpi-${metaKey}-value`}>
        {typeof value === "number" ? value.toLocaleString() : (value ?? 0)}
      </div>
      {to && <div className="text-[10px] font-mono text-orange-400/70 mt-2">View →</div>}
    </>
  );
  const cls = "relative block overflow-hidden bg-slate-900/80 border border-slate-800 rounded-md p-4 transition-colors " + (to ? "hover:border-orange-500/60 cursor-pointer" : "hover:border-slate-600");
  return to
    ? <Link to={to} className={cls} data-testid={`kpi-${metaKey}`}>{inner}</Link>
    : <div className={cls} data-testid={`kpi-${metaKey}`}>{inner}</div>;
}

const KPI_LINK = {
  raw_materials_available: "/inventory", finished_goods_available: "/inventory",
  low_stock_items: "/inventory", out_of_stock_items: "/inventory",
  open_purchase_orders: "/procurement", supplier_deliveries_expected: "/procurement",
  active_production_orders: "/production", production_waiting_materials: "/production",
  pending_material_requests: "/production", open_stock_transfers: "/inventory",
  available_vehicles: "/logistics", vehicles_under_maintenance: "/logistics",
  deliveries_in_transit: "/logistics", delayed_deliveries: "/logistics",
};

function KpiGrid({ keys, kpis }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {keys.map((k) => <Kpi key={k} metaKey={k} value={kpis?.[k]} to={KPI_LINK[k]} />)}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
      <Icon className="h-4 w-4 text-orange-400" /> {children}
    </div>
  );
}

function ListPanel({ title, icon, empty, children, testid }) {
  return (
    <Panel className="p-4 space-y-3" >
      <SectionTitle icon={icon}>{title}</SectionTitle>
      <div className="space-y-2" data-testid={testid}>{children || <div className="text-sm text-slate-500 py-4 text-center">{empty}</div>}</div>
    </Panel>
  );
}

function IncomingList({ rows }) {
  if (!rows?.length) return null;
  return rows.map((r, i) => (
    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2 last:border-0">
      <div><span className="font-mono text-orange-400">{r.po_number}</span> <span className="text-slate-400">· {r.supplier}</span></div>
      <div className={`text-xs font-mono ${r.overdue ? "text-orange-400" : "text-slate-500"}`}>{r.overdue ? "OVERDUE" : (r.expected_date ? String(r.expected_date).slice(0, 10) : "—")}</div>
    </div>
  ));
}

function ProductionList({ rows }) {
  if (!rows?.length) return null;
  return rows.map((r, i) => (
    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2 last:border-0">
      <div><span className="font-mono text-orange-400">{r.request_number}</span> <span className="text-slate-400">· {r.department || "—"}</span></div>
      <span className="text-xs font-mono uppercase text-slate-400">{String(r.status).replace(/_/g, " ")}</span>
    </div>
  ));
}

function LowStockList({ rows }) {
  if (!rows?.length) return null;
  return rows.map((r, i) => (
    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2 last:border-0">
      <div><span className="font-mono text-slate-300">{r.sku}</span> <span className="text-slate-500">· {r.name}</span></div>
      <div className="text-xs font-mono text-amber-400">{r.on_hand?.toLocaleString()} / {r.reorder_level?.toLocaleString()}</div>
    </div>
  ));
}

function ExceptionsList({ rows }) {
  if (!rows?.length) return null;
  const color = { critical: "text-rose-400", warning: "text-amber-400", info: "text-sky-400" };
  return rows.map((r, i) => (
    <div key={i} className="flex items-start gap-2 text-sm border-b border-slate-800/60 pb-2 last:border-0">
      <TriangleAlert className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color[r.severity] || "text-slate-400"}`} />
      <span className="text-slate-300">{r.message}</span>
    </div>
  ));
}

function ProdReqPanel({ rows, canApprove, onAction }) {
  if (!rows?.length) return <div className="text-sm text-slate-500 py-4 text-center">No production requests awaiting approval.</div>;
  return rows.map((r) => (
    <div key={r.id} className="flex items-center justify-between gap-3 text-sm border-b border-slate-800/60 pb-2 last:border-0">
      <div className="min-w-0">
        <span className="font-mono text-orange-400">{r.request_number}</span> <span className="text-slate-300">{r.product_name || "—"}</span> <span className="text-slate-500">×{r.quantity}</span>
        <span className={`ml-1 text-[10px] font-mono uppercase ${r.is_feasible ? "text-emerald-400" : "text-rose-400"}`}>{r.is_feasible ? "possible" : "shortfall"}</span>
        <div className="text-[11px] text-slate-500">by {r.created_by_name || "—"}</div>
      </div>
      {canApprove && (
        <div className="flex-shrink-0 flex gap-1.5">
          <button onClick={() => onAction(r.id, "approve")} data-testid={`pr-approve-${r.request_number}`} className="text-xs px-2 py-1 rounded bg-emerald-600/15 text-emerald-400 border border-emerald-600/30">approve</button>
          <button onClick={() => onAction(r.id, "reject")} data-testid={`pr-reject-${r.request_number}`} className="text-xs px-2 py-1 rounded text-slate-400 hover:text-rose-400">reject</button>
        </div>
      )}
    </div>
  ));
}

function DashHeader({ title, subtitle, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">{title}</h1>
        <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
      </div>
      {right}
    </div>
  );
}

/* ---------------- Control Tower (management) ---------------- */
function ControlTower({ data, canSeed, onSeed, seeding, onRefresh, loading, prAction, canApprovePR }) {
  const kpis = data?.kpis || {};
  return (
    <div className="space-y-6" data-testid="control-tower">
      <DashHeader
        title="Logistics Control Tower"
        subtitle="What is happening across the company right now."
        right={
          <div className="flex items-center gap-2">
            <button onClick={onRefresh} data-testid="refresh-stats-button" className="flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-xs uppercase tracking-wider px-3 py-2 rounded-md transition-colors">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            {canSeed && (
              <button onClick={onSeed} disabled={seeding} data-testid="seed-sample-data-button" className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-md font-mono text-xs uppercase tracking-wider border border-orange-400/30">
                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseZap className="h-4 w-4" />} Seed Sample Data
              </button>
            )}
          </div>
        }
      />
      <KpiGrid keys={CONTROL_TOWER_KEYS} kpis={kpis} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ListPanel title="Incoming Deliveries" icon={PackageCheck} empty="No deliveries expected." testid="list-incoming"><IncomingList rows={data?.incoming_deliveries} /></ListPanel>
        <ListPanel title="Current Production Activity" icon={Factory} empty="No active production." testid="list-production"><ProductionList rows={data?.production_activity} /></ListPanel>
        <ListPanel title="Low Stock Alerts" icon={PackageSearch} empty="All items above reorder level." testid="list-lowstock"><LowStockList rows={data?.low_stock_alerts} /></ListPanel>
        <ListPanel title="Recent Operational Problems" icon={AlertTriangle} empty="No exceptions right now." testid="list-exceptions"><ExceptionsList rows={data?.exceptions} /></ListPanel>
        <ListPanel title="Pending Production Requests" icon={ClipboardCheck} empty="No production requests awaiting approval." testid="list-prod-requests"><ProdReqPanel rows={data?.production_requests} canApprove={canApprovePR} onAction={prAction} /></ListPanel>
      </div>
    </div>
  );
}

/* ---------------- Department dashboard ---------------- */
const SECTIONS = [
  { key: "procurement", perm: "procurement:view", title: "Procurement", icon: ClipboardList,
    kpis: ["open_purchase_orders", "supplier_deliveries_expected", "low_stock_items", "out_of_stock_items"],
    cta: { label: "Open Procurement", to: "/procurement" }, list: "incoming" },
  { key: "warehouse", perm: "warehouse:view", title: "Warehouse & Inventory", icon: PackageCheck,
    kpis: ["raw_materials_available", "finished_goods_available", "low_stock_items", "pending_material_requests", "open_stock_transfers"],
    cta: { label: "Open Inventory", to: "/inventory" }, list: "lowstock" },
  { key: "production", perm: "production:view", title: "Production", icon: Factory,
    kpis: ["active_production_orders", "production_waiting_materials", "raw_materials_available"],
    cta: { label: "Open Production", to: "/production" }, list: "production" },
  { key: "sales", perm: "sales:view", title: "Sales", icon: ClipboardList,
    kpis: ["finished_goods_available", "open_customer_orders", "orders_awaiting_dispatch"],
    cta: null, list: null },
  { key: "logistics", perm: "logistics:view", title: "Logistics & Dispatch", icon: Truck,
    kpis: ["orders_awaiting_dispatch", "deliveries_in_transit", "delayed_deliveries", "available_vehicles"],
    cta: null, list: null },
];

function DepartmentDashboard({ data, user, hasPerm, prAction, canApprovePR }) {
  const kpis = data?.kpis || {};
  const sections = SECTIONS.filter((s) => hasPerm(s.perm));
  return (
    <div className="space-y-8" data-testid="department-dashboard">
      <DashHeader title="My Workspace" subtitle={`Welcome back, ${user?.name || ""} — here is what needs your attention.`} />
      {sections.length === 0 && (
        <Panel className="p-8 text-center text-slate-400">Your workspace is being set up. No modules are assigned to your role yet.</Panel>
      )}
      {sections.map((s) => (
        <div key={s.key} className="space-y-3" data-testid={`dept-section-${s.key}`}>
          <div className="flex items-center justify-between">
            <SectionTitle icon={s.icon}>{s.title}</SectionTitle>
            {s.cta
              ? <Link to={s.cta.to} data-testid={`dept-cta-${s.key}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300">{s.cta.label} <ArrowRight className="h-4 w-4" /></Link>
              : <span className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Workflow coming soon</span>}
          </div>
          <KpiGrid keys={s.kpis} kpis={kpis} />
          {s.list === "incoming" && data?.incoming_deliveries?.length > 0 && (
            <ListPanel title="Expected Deliveries" icon={PackageCheck} testid="dept-list-incoming"><IncomingList rows={data.incoming_deliveries} /></ListPanel>
          )}
          {s.list === "lowstock" && data?.low_stock_alerts?.length > 0 && (
            <ListPanel title="Low Stock Alerts" icon={PackageSearch} testid="dept-list-lowstock"><LowStockList rows={data.low_stock_alerts} /></ListPanel>
          )}
          {s.list === "production" && data?.production_activity?.length > 0 && (
            <ListPanel title="Material Requests" icon={Factory} testid="dept-list-production"><ProductionList rows={data.production_activity} /></ListPanel>
          )}
          {s.key === "procurement" && data?.production_activity?.length > 0 && (
            <ListPanel title="Material Requests" icon={Factory} testid="dept-list-material"><ProductionList rows={data.production_activity} /></ListPanel>
          )}
          {s.key === "production" && (
            <ListPanel title="Pending Production Requests" icon={ClipboardCheck} testid="dept-list-prod-requests"><ProdReqPanel rows={data?.production_requests} canApprove={canApprovePR} onAction={prAction} /></ListPanel>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Driver dashboard ---------------- */
function DriverDashboard({ user }) {
  return (
    <div className="space-y-6" data-testid="driver-dashboard">
      <DashHeader title="My Deliveries" subtitle={`${user?.name || "Driver"} — your assigned deliveries appear here.`} />
      <Panel className="p-10 text-center">
        <Truck className="h-10 w-10 text-slate-600 mx-auto mb-4" />
        <div className="text-slate-300 font-semibold">No deliveries assigned yet</div>
        <div className="text-sm text-slate-500 mt-1">When dispatch assigns you a shipment, it will show here with start, arrive and deliver actions.</div>
      </Panel>
    </div>
  );
}

/* ---------------- Gate / Security dashboard ---------------- */
function GateDashboard({ user }) {
  return (
    <div className="space-y-6" data-testid="gate-dashboard">
      <DashHeader title="Security Gate" subtitle={`${user?.name || "Officer"} — vehicle entry & exit log.`} />
      <Panel className="p-10 text-center">
        <Truck className="h-10 w-10 text-slate-600 mx-auto mb-4" />
        <div className="text-slate-300 font-semibold">Gate log coming online</div>
        <div className="text-sm text-slate-500 mt-1">Record vehicle entry/exit and see vehicles currently inside the factory.</div>
      </Panel>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const role = user?.role || "";
  const isManagement = ["super_administrator", "administrator", "managing_director"].includes(role) || hasPerm("system:admin");
  const isDriver = role === "driver";
  const isGate = role === "security_gate";
  const isLogistics = ["logistics_manager", "logistics_staff", "fleet_manager"].includes(role);
  const isFinance = role === "finance_admin";
  const canApprovePR = hasPerm("production:approve");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/metrics");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isDriver || isGate || isLogistics || isFinance) { setLoading(false); return; }
    load();
  }, [load, isDriver, isGate, isLogistics, isFinance]);

  const prAction = async (id, action) => {
    try { await api.post(`/production-requests/${id}/${action}`); toast.success(`Request ${action} \u2713`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/seed");
      toast.success(`Sample data seeded · ${data.new_transactions} new ledger entries`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  if (isDriver) return <DriverDashboard user={user} />;
  if (isGate) return <GateDashboard user={user} />;
  if (isLogistics) return <Logistics />;
  if (isFinance) return <Finance />;

  if (loading && !data) {
    return <div className="py-24 text-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500 inline" /></div>;
  }

  if (isManagement) {
    return <ControlTower data={data} canSeed={hasPerm("seed:execute")} onSeed={seed} seeding={seeding} onRefresh={load} loading={loading} prAction={prAction} canApprovePR={canApprovePR} />;
  }
  return <DepartmentDashboard data={data} user={user} hasPerm={hasPerm} prAction={prAction} canApprovePR={canApprovePR} />;
}
